import { useState } from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';

import LogoImage from '../../../../static/image/lp/logo.svg';
import LogoImageSmall from '../../../../static/image/logo.svg';
import { ReactComponent as IconMail } from '../../../../static/image/icon_login_mail.svg';

import { Z_INDEX_APPBAR } from '../../../../const/ZIndex';
import { AccountSubscriptionLpButton } from '../../../atoms/lp/accountSubscription/AccountSubscriptionLpButton';

interface AccountSubscriptionHeaderProps {
  onClickCv?: () => void;
}

export const AccountSubscriptionHeader: React.FC<AccountSubscriptionHeaderProps> = ({ onClickCv }): JSX.Element => {
  const { navOpen, toggleNav } = useAccountSubscriptionHeader();

  const handleClickCv = onClickCv ?? (() => void 0);

  return (
    <Header>
      <Content>
        <Inner>
          <SpMenuButton active={navOpen} onClick={toggleNav}>
            <span></span>
          </SpMenuButton>
          <PcLogo src={LogoImage} alt="株式会社SAMURAI" />
          <SpLogo src={LogoImageSmall} alt="株式会社SAMURAI" />
          <PcContactButton active={navOpen}>
            <PcContactLink href="https://sejuku.net/contact/?cid=sns_lp_tocontact">
              <IconMail className="iconMail" />
              お問い合わせ
            </PcContactLink>
          </PcContactButton>
          <PcCvButton onClick={handleClickCv} isShowSubtitle={false} subscription />
          <SpCvLink onClick={handleClickCv}>有料プラン加入</SpCvLink>
        </Inner>
      </Content>
      <SpNav active={navOpen}>
        <SpContactButton href="https://sejuku.net/contact/?cid=sns_lp_tocontact">お問い合わせ</SpContactButton>
      </SpNav>
    </Header>
  );
};

const useAccountSubscriptionHeader = () => {
  const [navOpen, setNavOpen] = useState(false);

  const toggleNav = () => {
    setNavOpen(!navOpen);
  };

  return { navOpen, toggleNav };
};

const Header = styled.header`
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: ${Z_INDEX_APPBAR};
`;
const Content = styled.div`
  padding: 1rem 0 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  ${media.lessThan('medium')`
    padding: 0.75rem 0;
    border-bottom: none;
  `}
`;
const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;

  ${media.lessThan('large')`
    padding: 0 2rem;
  `}
  ${media.lessThan('medium')`
    width: 100%;
    justify-content: space-between;
    flex-direction: row;
    padding: 0 1rem;
    position: relative;
  `}
`;
const StyledLink = styled.a`
  display: block;
  width: 100%;
  max-width: 160px;
  padding: 0.875rem;
  border: 1px solid #e2001b;
  border-radius: 4px;
  box-sizing: border-box;
  color: #e2001b;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1rem;
  text-align: center;

  ${media.lessThan('large')`
    flex: 1;
  `}
`;

const PcLogo = styled.img`
  max-height: 24px;

  ${media.lessThan('large')`
    width: 30%;
  `}
  ${media.lessThan('medium')`
    display: none;
  `}
  ${media.lessThan('small')`
    width: 7.5rem;
  `}
`;
const PcContactLink = styled(StyledLink)`
  .iconMail {
    path {
      fill: #e2001b;
    }
    width: 1rem;
    height: 1rem;
    line-height: 1rem;
    vertical-align: bottom;
    margin-right: 0.5rem;
    display: inline-block;
  }
`;
const PcContactButton = styled.div<{ active: boolean }>`
  display: flex;
  justify-content: flex-end;
  flex: 1;
  margin: 0 1rem;

  ${media.lessThan('large')`
    margin-left: 2rem;
  `}

  ${media.lessThan('medium')`
    display: none;
  `}

  opacity: ${(props) => (props.active ? '0' : '1')};
  pointer-events: ${(props) => (props.active ? 'none' : 'all')};
`;
const PcCvButton = styled(AccountSubscriptionLpButton)`
  max-width: 160px;
  font-weight: 700;
  line-height: 1rem;
  text-align: center;

  &:before {
    box-shadow: none;
  }

  ${media.lessThan('medium')`
    display: none;
  `}

  span {
    font-size: 0.875rem !important;
  }
`;

const SpMenuButton = styled.div<{ active: boolean }>`
  display: none;
  margin-left: 0;

  ${media.lessThan('medium')`
    display: block;
    width: 1.5rem;
    height: 1.5rem;
    margin-left: 1rem;
    position: relative;
    flex: 1;

    span {
      display: block;
      width: 1.125rem;
      height: 0.125rem;
      margin: auto 0;
      background: #000;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      transition: all .2s;

      &:before,
      &:after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        background: #000;
        position: absolute;
        left: 0;
        transition: all .2s;
      }

      &:before {
        top: -0.3125rem;
      }

      &:after {
        bottom: -0.3125rem;
      }
    }
  `}

  ${(props) =>
    props.active
      ? media.lessThan('medium')`
    span {
      background: rgba(0,0,0,0);

      &:before {
        top: 0;
        transform: rotate(45deg);
      }

      &:after {
        bottom: 0;
        transform: rotate(-45deg);
      }
    }
  `
      : ''}

  ${media.lessThan('small')`
    margin-left: 0.5rem;
  `}
`;
const SpLogo = styled(PcLogo)`
  display: none;
  flex: 1;

  ${media.lessThan('medium')`
    display: block;
  `}
`;
const SpNav = styled.nav<{ active: boolean }>`
  display: none;

  ${media.lessThan('medium')`
    display: block;
    width: 100%;
    height: 0;
    padding: 2.75rem 2rem 2.5rem;
    background: rgba(255,255,255,0.96);
    box-sizing: border-box;
    position: absolute;
    top: calc(100% + 1px);
    left: 0;
    transition: all .2s;
    opacity: 0;
    pointer-events: none;
    overflow-y: scroll;
  `}

  ${(props) =>
    props.active
      ? media.lessThan('medium')`
    height: calc(100vh - 57px);
    opacity: 1;
    pointer-events: all;
  `
      : ''}
`;
const SpContactButton = styled.a`
  display: block;
  margin-top: 1.5rem;
  padding: 0.75rem 0;
  border: 1px solid #e2001b;
  border-radius: 0.25rem;
  color: #e2001b;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.375rem;
  text-align: center;
`;
const SpCvLink = styled.p`
  display: none;
  font-size: 0.75rem;
  color: #eb0000;
  flex: 1;
  text-align: right;
  cursor: pointer;

  ${media.lessThan('medium')`
    display: block;
  `}
`;
